import React, { useEffect, useState, useCallback } from "react";
import { subDays } from "date-fns";
import Calendar from "react-calendar";
import { useDispatch, useSelector } from "react-redux";

import { PlayBackWhite } from "../../assets/images";
import { playbackActions } from "../../redux/features/shared/playback";
import {
    multiTimeZoneHelper,
    replaceTimeToDate,
    subtractMinutes,
} from "../../utils/utils";

import TimerInput from "./TimerInput";
import {
  fetchRecordingStatusByDevicesId
} from "../../services/camera/cameraService";

const Playback = ({
    selectedMoreThanOne,
    handlePlaybackClick,
    selectedDeviceTimeZoneOffsetMinutes,
    deviceId,
}) => {
    const dispatch = useDispatch();

    const playbackState = useSelector((state) => state?.playback);

    const {
        startDate,
        defaultEndDate,
        timerInputState,
        endDate,
    } = playbackState;

    const { timeString } = timerInputState;

    const [endTimeString, setEndTimeString] = useState("");
    const [activeDateInput, setActiveDateInput] = useState("start");
    const [recordingStatusMap, setRecordingStatusMap] = useState({});

    const padTime = (value) => String(value).padStart(2, "0");

    const getDateKey = (year, month, day) => {
        return `${year}-${String(month).padStart(2, "0")}-${String(
            day
        ).padStart(2, "0")}`;
    };

    const fetchCalendarStatus = useCallback(
        async (rangeStartDate, rangeEndDate) => {

            if (selectedMoreThanOne) {
                setRecordingStatusMap({});
                return;
            }
            try {
                const response = await fetchRecordingStatusByDevicesId({
                    // deviceId:334,
                    deviceId:deviceId,
                    startDate: rangeStartDate.toISOString().split("T")[0],
                    endDate: new Date() < rangeEndDate ? new Date().toISOString().split("T")[0] : rangeEndDate.toISOString().split("T")[0],

                });

                if (
                    response?.Status === "success" &&
                    Array.isArray(response?.Data)
                ) {
                    const map = {};

                    response.Data.forEach((item) => {
                        const key = getDateKey(item.Year, item.Month, item.Day);

                        map[key] = item.Status;
                    });

                    setRecordingStatusMap(map);
                } else {
                    setRecordingStatusMap({});
                }
            } catch (error) {
                console.error("Recording status fetch failed", error);
            }
        },
        [deviceId,selectedMoreThanOne]
    );

const getVisibleRange = (activeStartDate) => {

    const firstDayOfMonth = new Date(
        activeStartDate.getFullYear(),
        activeStartDate.getMonth(),
        1
    );

    // Monday = first day of week
    const day = firstDayOfMonth.getDay();

    // Convert JS Sunday(0) => 6
    const mondayOffset =
        day === 0
            ? 6
            : day - 1;

    const visibleStart = new Date(firstDayOfMonth);

    visibleStart.setDate(
        firstDayOfMonth.getDate() -
        mondayOffset
    );

    const visibleEnd = new Date(visibleStart);

    // 6 rows × 7 days
    visibleEnd.setDate(
        visibleStart.getDate() + 42
    );

    return {
        start: visibleStart,
        end: visibleEnd
    };
};

    useEffect(() => {
        let currDateTime = multiTimeZoneHelper(
            selectedDeviceTimeZoneOffsetMinutes
        );

        let initialStartDate = subtractMinutes(currDateTime, defaultEndDate);

        dispatch(playbackActions.setEndDate(currDateTime.toString()));

        dispatch(playbackActions.setStartDate(initialStartDate.toString()));

        const timerStr = `${padTime(initialStartDate.getHours())}_${padTime(
            initialStartDate.getMinutes()
        )}_${padTime(initialStartDate.getSeconds())}`;

        const endTimerStr = `${padTime(currDateTime.getHours())}_${padTime(
            currDateTime.getMinutes()
        )}_${padTime(currDateTime.getSeconds())}`;

        dispatch(playbackActions.setTimerInputTimeString(timerStr));

        setEndTimeString(endTimerStr);
    }, [dispatch, defaultEndDate, selectedDeviceTimeZoneOffsetMinutes]);

    useEffect(() => {
        if (endDate) {
            const endDateObj = new Date(endDate);

            const endTimerStr = `${padTime(endDateObj.getHours())}_${padTime(
                endDateObj.getMinutes()
            )}_${padTime(endDateObj.getSeconds())}`;

            setEndTimeString(endTimerStr);
        }
    }, [endDate]);

    // Initial calendar load
    useEffect(() => {

        if (!deviceId || selectedMoreThanOne) {
            setRecordingStatusMap({});
            return;
        }

        const currentMonthStart = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            1
        );

        const range = getVisibleRange(currentMonthStart);

        fetchCalendarStatus(range.start, range.end);
        
    }, [deviceId, fetchCalendarStatus,selectedMoreThanOne]);

    const handleMonthChange = ({ activeStartDate, view }) => {
        if (view !== "month") return;

        if (selectedMoreThanOne) {
            setRecordingStatusMap({});
            return;
        }

        const range = getVisibleRange(activeStartDate);

        fetchCalendarStatus(range.start, range.end);
    };

    const handleStartDateChange = (value) => {
        const newStartDate = replaceTimeToDate(value, timeString);

        dispatch(playbackActions.setStartDate(newStartDate.toString()));
    };

    const handleEndDateChange = (value) => {
        const newEndDate = replaceTimeToDate(value, endTimeString);

        dispatch(playbackActions.setEndDate(newEndDate.toString()));
    };

    const handleStartTimeChange = (data, type) => {
        let arr = timeString.split("_");

        let value = data.toString().padStart(2, "0");

        if (type === "HOUR") arr[0] = value;
        else if (type === "MIN") arr[1] = value;
        else arr[2] = value;

        const updatedStartDate = replaceTimeToDate(startDate, arr.join("_"));

        dispatch(playbackActions.setTimerInputTimeString(arr.join("_")));

        dispatch(playbackActions.setStartDate(updatedStartDate.toString()));
    };

    const handleEndTimeChange = (data, type) => {
        let arr = endTimeString.split("_");

        let value = data.toString().padStart(2, "0");

        if (type === "HOUR") arr[0] = value;
        else if (type === "MIN") arr[1] = value;
        else arr[2] = value;

        const updatedEndDate = replaceTimeToDate(endDate, arr.join("_"));

        setEndTimeString(arr.join("_"));

        dispatch(playbackActions.setEndDate(updatedEndDate.toString()));
    };

    const handleCalendarChange = (value) => {
        if (activeDateInput === "start") {
            handleStartDateChange(value);
        } else {
            handleEndDateChange(value);
        }
    };

    const getTileClassName = ({ date, view }) => {

        if (selectedMoreThanOne) {
            return "";
        }
        if (view !== "month") {
            return "";
        }

        const key = getDateKey(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate()
        );

        const status = recordingStatusMap[key];

        switch (status) {
            case 0:
                return "recording-red";

            case 1:
                return "recording-orange";

            case 2:
                return "recording-green";

            default:
                return "";
        }
    };

    const getCardStyles = (type) => ({
        border:
            activeDateInput === type
                ? "2px solid #007bff"
                : "1px solid #dcdcdc",
        boxShadow:
            activeDateInput === type
                ? "0 0 0 4px rgba(0,123,255,0.12)"
                : "none",
        borderRadius: "10px",
        cursor: "pointer",
        padding: "1rem",
        background: "#fff",
    });

    const activeCalendarValue =
        activeDateInput === "start" ? new Date(startDate) : new Date(endDate);

    return (
        <>
            {/* Inline styles */}
            <style>
                {`
                    .recording-red {
                        position: relative;
                    }

                    .recording-red::after {
                        content: "";
                        position: absolute;
                        left: 3px;
                        right: 3px;
                        bottom: 2px;
                        height: 4px;
                        background: #ff4d4f;
                        border-radius: 10px;
                    }

                    .recording-orange {
                        position: relative;
                    }

                    .recording-orange::after {
                        content: "";
                        position: absolute;
                        left: 3px;
                        right: 3px;
                        bottom: 2px;
                        height: 4px;
                        background: #fa8c16;
                        border-radius: 10px;
                    }

                    .recording-green {
                        position: relative;
                    }

                    .recording-green::after {
                        content: "";
                        position: absolute;
                        left: 3px;
                        right: 3px;
                        bottom: 2px;
                        height: 4px;
                        background: #52c41a;
                        border-radius: 10px;
                    }

                    .react-calendar__tile--active.recording-red {
                        background: #ff4d4f !important;
                        color: #fff !important;
                    }

                    .react-calendar__tile--active.recording-orange {
                        background: #fa8c16 !important;
                        color: #fff !important;
                    }

                    .react-calendar__tile--active.recording-green {
                        background: #52c41a !important;
                        color: #fff !important;
                    }
                `}
            </style>

            {startDate?.length > 0 && (
                <>
                    <div
                        className="popupDiv"
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "1rem",
                        }}
                    >
                        <div
                            className="popup_card"
                            style={{
                                flex: 1,
                                minWidth: "300px",
                            }}
                        >
                            <p className="popup_header">
                                Select{" "}
                                {activeDateInput === "start" ? "Start" : "End"}{" "}
                                Date
                            </p>

                            <Calendar
                                onChange={handleCalendarChange}
                                value={activeCalendarValue}
                                maxDate={new Date()}
                                // minDate={subDays(new Date(), 180)}
                                // showFixedNumberOfWeeks={true}
                                onActiveStartDateChange={handleMonthChange}
                                tileClassName={getTileClassName}
                            />
                            {/* <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "16px",
                                    marginTop: "12px",
                                    flexWrap: "wrap",
                                    fontSize: "12px"
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}
                                >
                                    <span
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            background: "#52c41a",
                                            display: "inline-block"
                                        }}
                                    />
                                    <span>Recording Available</span>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}
                                >
                                    <span
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            background: "#fa8c16",
                                            display: "inline-block"
                                        }}
                                    />
                                    <span>Partial Recording</span>
                                </div>

                            </div> */}
                        </div>

                        <div
                            style={{
                                flex: 1,
                                minWidth: "300px",
                                display: "flex",
                                gap: "1rem",
                            }}
                        >
                            <div
                                className="popup_card"
                                style={getCardStyles("start")}
                                onClick={() => setActiveDateInput("start")}
                                tabIndex={0}
                            >
                                <p className="popup_header">Start Time</p>

                                <div
                                    className="popup_cardTimer"
                                    style={{
                                        marginTop: "1rem",
                                    }}
                                >
                                    <div
                                        className="popup_cardTimerRight"
                                        style={{
                                            width: "100%",
                                            border: "none",
                                        }}
                                    >
                                        <TimerInput
                                            timeString={timeString}
                                            onTimerInputCB={
                                                handleStartTimeChange
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className="popup_card"
                                style={getCardStyles("end")}
                                onClick={() => setActiveDateInput("end")}
                                tabIndex={0}
                            >
                                <p className="popup_header">End Time</p>

                                <div
                                    className="popup_cardTimer"
                                    style={{
                                        marginTop: "1rem",
                                    }}
                                >
                                    <div
                                        className="popup_cardTimerRight"
                                        style={{
                                            width: "100%",
                                            border: "none",
                                        }}
                                    >
                                        <TimerInput
                                            timeString={endTimeString}
                                            onTimerInputCB={handleEndTimeChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="popup_link" onClick={handlePlaybackClick}>
                        <img
                            src={PlayBackWhite}
                            className="playbackicon mr-2"
                            alt="playback icon"
                        />

                        <span>Playback</span>
                    </div>
                </>
            )}
        </>
    );
};

export default Playback;
